import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Button, Avatar, Chip, Stack } from '@mui/material';
import { FiFilter, FiX } from 'react-icons/fi';

import Actions from '../../constants/Actions';
import { deriveFilterOptions, countActiveFilters } from '../../utils/filterOptions';
import FilterSection from './FilterSection';
import PriceRange from './PriceRange';

const ProductFilters = ({ onClose }) => {
    const dispatch = useDispatch();
    const products = useSelector((state) => state.products);
    const filters = useSelector((state) => state.filters);
    const filialOptions = useSelector((state) => state.filialOptions);

    const derived = React.useMemo(() => deriveFilterOptions(products), [products]);
    const active = countActiveFilters(filters);

    const setFilter = (key, value) => {
        dispatch({ type: Actions.SET_FILTER, key, value });
    };

    const handleReset = () => {
        dispatch({ type: Actions.RESET_FILTERS });
    };

    const handlePriceChange = ({ precoMin, precoMax }) => {
        dispatch({ type: Actions.SET_FILTER, key: 'precoMin', value: precoMin });
        dispatch({ type: Actions.SET_FILTER, key: 'precoMax', value: precoMax });
    };

    const filialMapped = React.useMemo(() => {
        if (!Array.isArray(filialOptions)) return [];
        return filialOptions
            .map((f) => {
                const value = f.codigo_filial ?? f.codigo ?? f.cod_filial;
                if (value === undefined || value === null) return null;
                const label = f.nome_fantasia || f.descricao || String(value);
                return { value: String(value), label: String(label) };
            })
            .filter(Boolean);
    }, [filialOptions]);

    const filialLabelByValue = React.useMemo(() => {
        const map = new Map();
        filialMapped.forEach((f) => map.set(f.value, f.label));
        return map;
    }, [filialMapped]);

    const removeFromList = (key, value) => {
        const list = Array.isArray(filters[key]) ? filters[key] : [];
        setFilter(key, list.filter((v) => v !== value));
    };

    const clearPrice = () => {
        setFilter('precoMin', null);
        setFilter('precoMax', null);
    };

    const renderChips = (key, list, labelResolver) => {
        if (!Array.isArray(list) || list.length === 0) return null;
        return list.map((value) => (
            <Chip
                key={`${key}-${value}`}
                label={labelResolver ? labelResolver(value) : value}
                size="small"
                onDelete={() => removeFromList(key, value)}
                deleteIcon={<FiX size={12} />}
                sx={{
                    bgcolor: '#1a1a2e',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 600,
                    height: 22,
                    '& .MuiChip-deleteIcon': { color: '#fff', mr: 0.5 },
                    '& .MuiChip-deleteIcon:hover': { color: '#ddd' }
                }}
            />
        ));
    };

    const hasPriceActive =
        filters.precoMin !== null && filters.precoMin !== undefined
        || filters.precoMax !== null && filters.precoMax !== undefined;

    return (
        <Box sx={{ bgcolor: '#fff', borderRadius: { md: 2 }, border: { md: '1px solid' }, borderColor: { md: 'grey.200' } }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'grey.200'
                }}
            >
                <Avatar sx={{ width: 28, height: 28, bgcolor: '#e8eaf0', color: '#1a1a2e' }}>
                    <FiFilter size={14} />
                </Avatar>
                <Typography sx={{ fontWeight: 700, fontSize: 14, flex: 1 }}>
                    Filtros
                </Typography>
                {active > 0 && (
                    <Box
                        sx={{
                            px: 0.75,
                            minWidth: 20,
                            height: 20,
                            borderRadius: 10,
                            bgcolor: '#1a1a2e',
                            color: '#fff',
                            fontSize: 11,
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        {active}
                    </Box>
                )}
                {onClose && (
                    <Box
                        component="button"
                        onClick={onClose}
                        sx={{
                            border: 'none',
                            bgcolor: 'transparent',
                            cursor: 'pointer',
                            p: 0.5,
                            display: 'flex',
                            color: 'text.secondary'
                        }}
                    >
                        <FiX size={18} />
                    </Box>
                )}
            </Box>

            {active > 0 && (
                <Box sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'grey.200' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 600, flex: 1 }}>
                            Selecionados ({active})
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleReset}
                            startIcon={<FiX size={12} />}
                            sx={{
                                textTransform: 'none',
                                fontSize: 11,
                                fontWeight: 600,
                                py: 0.25,
                                px: 1,
                                minHeight: 24,
                                borderColor: '#1a1a2e',
                                color: '#1a1a2e',
                                '&:hover': { borderColor: '#1a1a2e', bgcolor: '#f0f1f5' }
                            }}
                        >
                            Limpar
                        </Button>
                    </Box>
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                        {renderChips('griffe', filters.griffe)}
                        {renderChips('grupo', filters.grupo)}
                        {renderChips('subgrupo', filters.subgrupo)}
                        {renderChips('grade', filters.grade)}
                        {renderChips('filial', filters.filial, (v) => filialLabelByValue.get(v) || v)}
                        {hasPriceActive && (
                            <Chip
                                key="preco"
                                label={`R$ ${filters.precoMin ?? 0} - ${filters.precoMax ?? '∞'}`}
                                size="small"
                                onDelete={clearPrice}
                                deleteIcon={<FiX size={12} />}
                                sx={{
                                    bgcolor: '#1a1a2e',
                                    color: '#fff',
                                    fontSize: 11,
                                    fontWeight: 600,
                                    height: 22,
                                    '& .MuiChip-deleteIcon': { color: '#fff', mr: 0.5 },
                                    '& .MuiChip-deleteIcon:hover': { color: '#ddd' }
                                }}
                            />
                        )}
                    </Stack>
                </Box>
            )}

            <Box>
                <FilterSection
                    title="Griffe"
                    options={derived.griffe}
                    selected={filters.griffe}
                    onChange={(v) => setFilter('griffe', v)}
                    defaultExpanded
                />
                <FilterSection
                    title="Grupo"
                    options={derived.grupo}
                    selected={filters.grupo}
                    onChange={(v) => setFilter('grupo', v)}
                    defaultExpanded
                />
                <FilterSection
                    title="Subgrupo"
                    options={derived.subgrupo}
                    selected={filters.subgrupo}
                    onChange={(v) => setFilter('subgrupo', v)}
                />
                <FilterSection
                    title="Grade"
                    options={derived.grade}
                    selected={filters.grade}
                    onChange={(v) => setFilter('grade', v)}
                />
                <FilterSection
                    title="Filial"
                    options={filialMapped}
                    selected={filters.filial}
                    onChange={(v) => setFilter('filial', v)}
                />
                <PriceRange
                    precoMin={filters.precoMin}
                    precoMax={filters.precoMax}
                    onChange={handlePriceChange}
                />
            </Box>
        </Box>
    );
};

export default ProductFilters;
